import { useState } from 'react'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Badge, ScrollArea } from '@creanizer/ui'
import { Search, Trash2, Eye, Edit, Globe, Download, Folder } from 'lucide-react'
import type { SavedWorldBuilding } from '@creanizer/types'
import { useWorldBuilderStore } from '../../../../../stores/worldbuilderStore'
import { useFolderStore } from '../../../../../stores/folderStore'
import { WorldBuilderEditModal } from '../WorldBuilderEditModal'
import { ExportImportButtons } from '../../../../../components/common'
import { FolderManager, FolderMoveSelect } from '../FolderManager'

interface SavedWorldsListProps {
  worlds: SavedWorldBuilding[]
  onLoad: (world: SavedWorldBuilding) => void
}

export function SavedWorldsList({ worlds, onLoad }: SavedWorldsListProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [editingWorld, setEditingWorld] = useState<SavedWorldBuilding | null>(null)
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [showFolders, setShowFolders] = useState(true)
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null)
  
  const { deleteWorld, clearWorlds, exportAsText, exportAsJSON, exportSingleAsText, exportSingleAsJSON, importFromJSON, updateWorld } = useWorldBuilderStore()
  const { getItemsInFolder, getItemFolder, moveItemBetweenFolders } = useFolderStore()
  
  // フォルダによるフィルタリング
  let folderFilteredWorlds = worlds
  if (selectedFolderId === 'all') {
    folderFilteredWorlds = worlds
  } else if (selectedFolderId === null) {
    // 未整理: どのフォルダにも属していないアイテム
    const allFolderItemIds = new Set<string>()
    const folders = useFolderStore.getState().getFolders('world')
    folders.forEach(folder => {
      getItemsInFolder('world', folder.id).forEach(id => allFolderItemIds.add(id))
    })
    folderFilteredWorlds = worlds.filter(world => !allFolderItemIds.has(world.id))
  } else {
    const itemIds = getItemsInFolder('world', selectedFolderId)
    folderFilteredWorlds = worlds.filter(world => itemIds.includes(world.id))
  }
  
  // 検索によるフィルタリング
  const filteredWorlds = folderFilteredWorlds.filter(world =>
    world.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    world.result.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    world.result.concept.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (world.tags && world.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())))
  )

  const handleExportAsJSON = () => {
    const data = exportAsJSON()
    if (!data) return
    
    const blob = new Blob([data], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `saved_worlds_${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="card max-w-full overflow-x-hidden">
      <div className="flex items-center gap-2 mb-2">
        <Globe className="h-5 w-5" />
        <h2 className="text-xl font-semibold">
          保存済み世界観 ({worlds.length}/500)
        </h2>
      </div>
      <p className="text-muted-foreground mb-6">
        生成した世界観設定を保存・管理
      </p>
      
      <div className="space-y-4 max-w-full">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="世界観を検索..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFolders(!showFolders)}
            className="px-2"
          >
            <Folder className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex gap-2">
          <ExportImportButtons
            onExportText={exportAsText}
            onExportJSON={handleExportAsJSON}
            onImportJSON={importFromJSON}
            dataTypeName="世界観"
          />

          {worlds.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (confirm('すべての世界観を削除しますか？')) {
                  clearWorlds()
                }
              }}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              全削除
            </Button>
          )}
        </div>

        <div className="flex gap-4">
          {/* フォルダサイドバー */}
          {showFolders && (
            <div className="w-48 border-r pr-4">
              <FolderManager
                generatorType="world"
                items={worlds}
                selectedFolderId={selectedFolderId}
                onFolderSelect={setSelectedFolderId}
                draggedItemId={draggedItemId}
                onItemMove={(itemId, toFolderId) => {
                  const currentFolder = getItemFolder('world', itemId)
                  moveItemBetweenFolders('world', itemId, currentFolder?.id || null, toFolderId)
                }}
              />
            </div>
          )}
          
          {/* 世界観リスト */}
          <div className="flex-1">
            <ScrollArea className="h-[500px] w-full overflow-x-hidden">
              <div className="space-y-2 max-w-full w-full">
                {filteredWorlds.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    {searchQuery ? '検索結果がありません' : '保存済みの世界観がありません'}
                  </p>
                ) : (
                  filteredWorlds.map((world) => (
                    <div 
                      key={world.id} 
                      className="card p-2 sm:p-4 overflow-hidden max-w-full w-[calc(100%-2rem)] sm:w-full mr-auto cursor-move hover:shadow-lg transition-shadow"
                      draggable
                      onDragStart={(e) => {
                        setDraggedItemId(world.id)
                        e.dataTransfer.effectAllowed = 'move'
                      }}
                      onDragEnd={() => {
                        setDraggedItemId(null)
                      }}
                      style={{
                        opacity: draggedItemId === world.id ? 0.5 : 1
                      }}
                    >
                  {/* モバイル版: 縦並びレイアウト */}
                  <div className="flex flex-col gap-3 sm:hidden w-full">
                    <div className="w-full">
                      <h3 className="font-medium text-sm truncate">{world.result.name}</h3>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                        <span className="truncate">{world.result.worldScale}</span>
                        <span>•</span>
                        <span>魔法:{world.result.parameters.magicLevel}</span>
                        <span>•</span>
                        <span>科学:{world.result.parameters.scienceLevel}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2 truncate">
                        {world.result.concept}
                      </p>
                      
                      {world.tags && world.tags.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {world.tags.slice(0, 2).map((tag, i) => (
                            <span key={i} className="px-2 py-1 bg-primary/10 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(world.createdAt).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      
                      {/* フォルダ選択 */}
                      <div className="mt-2">
                        <FolderMoveSelect
                          generatorType="world"
                          itemId={world.id}
                          currentFolderId={getItemFolder('world', world.id)?.id}
                          onMove={() => {}}
                        />
                      </div>
                    </div>
                    
                    {/* モバイル版: 横一列のボタン */}
                    <div className="flex gap-0.5 justify-start w-full">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onLoad(world)}
                        title="読込"
                        className="w-12 h-7 px-1"
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingWorld(world)}
                        title="編集"
                        className="w-12 h-7 px-1"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportSingleAsText(world.id)}
                        title="テキスト形式でエクスポート"
                        className="w-14 h-7 px-0.5 text-[9px]"
                      >
                        <Download className="h-2.5 w-2.5" />
                        <span className="ml-0.5">txt</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportSingleAsJSON(world.id)}
                        title="JSON形式でエクスポート"
                        className="w-14 h-7 px-0.5 text-[9px]"
                      >
                        <Download className="h-2.5 w-2.5" />
                        <span className="ml-0.5">json</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (confirm('この世界観を削除しますか？')) {
                            deleteWorld(world.id)
                          }
                        }}
                        className="w-12 h-7 px-1"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* PC版: 既存の横並びレイアウト */}
                  <div className="hidden sm:flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-sm truncate">{world.result.name}</h3>
                        {(world as any).isFavorite && <Badge variant="secondary">お気に入り</Badge>}
                      </div>
                      
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                        <span className="truncate">{world.result.worldScale}</span>
                        <span>•</span>
                        <span>魔法:{world.result.parameters.magicLevel}</span>
                        <span>•</span>
                        <span>科学:{world.result.parameters.scienceLevel}</span>
                        <span>•</span>
                        <span>文明:{world.result.parameters.civilizationLevel}</span>
                      </div>
                      
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {world.result.concept}
                      </p>
                      
                      {world.tags && world.tags.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {world.tags.slice(0, 2).map((tag, i) => (
                            <span key={i} className="px-2 py-1 bg-primary/10 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(world.createdAt).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      
                      {/* フォルダ選択（PC版） */}
                      <div className="mt-2 w-32">
                        <FolderMoveSelect
                          generatorType="world"
                          itemId={world.id}
                          currentFolderId={getItemFolder('world', world.id)?.id}
                          onMove={() => {}}
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onLoad(world)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          読込
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportSingleAsText(world.id)}
                          title="テキスト形式でエクスポート"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          txt
                        </Button>
                      </div>
                      
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingWorld(world)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          編集
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportSingleAsJSON(world.id)}
                          title="JSON形式でエクスポート"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          json
                        </Button>
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (confirm('この世界観を削除しますか？')) {
                            deleteWorld(world.id)
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
              </div>
            </ScrollArea>
          </div>
        </div>

        {worlds.length >= 450 && (
          <p className="text-xs text-muted-foreground text-center">
            保存可能件数の上限（500件）に近づいています
          </p>
        )}
      </div>
      
      {editingWorld && (
        <WorldBuilderEditModal
          isOpen={!!editingWorld}
          onClose={() => setEditingWorld(null)}
          onSave={(updatedResult) => {
            updateWorld(editingWorld.id, {
              result: { ...editingWorld.result, ...updatedResult },
              updatedAt: new Date().toISOString()
            })
            setEditingWorld(null)
          }}
          worldResult={editingWorld.result}
          title={`「${editingWorld.result.name}」を編集`}
        />
      )}
    </div>
  )
}